import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, RotateCcw, Save } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/rich-text-editor";

type Language = "lt" | "en";
type TemplateFormat = "text" | "html";

// Plain-text → HTML: escape, then turn newlines into <br> and blank-line
// separated blocks into paragraphs so existing templates read naturally once
// switched to HTML.
function textToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .split(/\n{2,}/)
    .map((block) => `<p>${escape(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// HTML → plain text for when an author switches a template back to plain text.
function htmlToText(html: string): string {
  const el = document.createElement("div");
  el.innerHTML = html
    .replace(/<\/(p|div|h[1-4]|li|blockquote|tr)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  return (el.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

type TemplateItem = NonNullable<
  ReturnType<typeof trpc.emailTemplates.list.useQuery>["data"]
>[number];

export function SettingsEmailTemplatesPage() {
  const [language, setLanguage] = useState<Language>("lt");
  const list = trpc.emailTemplates.list.useQuery({ language });

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6">
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Email templates</h2>
            <p className="text-sm text-muted-foreground">
              Templates used by the “Message to client” modal on the
              opportunity page. Switch a template to <strong>HTML</strong> to add
              rich formatting and images/GIFs (paste a GIF URL with the image
              button). The opportunity’s language determines which version is
              shown. Supported variables:{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{company}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_first_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_last_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_reference_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{opportunity_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{landing_url}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{user_name}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{user_phone}}"}</code>{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{user_email}}"}</code>.
              Set the per-opportunity{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_reference_name}}"}</code>{" "}
              when you want a custom greeting that differs from the contact's name —
              it also overrides{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{"{{contact_name}}"}</code>{" "}
              in the rendered email.
              In LT templates the recipient’s name is automatically declined to the
              vocative case (e.g. “Artūras” → “Artūrai”) unless a reference name is set.
            </p>
          </div>
          <div className="inline-flex rounded-md border bg-background p-0.5 text-sm">
            {(["lt", "en"] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLanguage(lang)}
                className={
                  "px-3 py-1 rounded-sm font-medium transition-colors " +
                  (language === lang
                    ? "bg-zinc-900 text-white"
                    : "text-muted-foreground hover:text-foreground")
                }
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      {list.isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-4">
          {(list.data ?? []).map((tpl) => (
            <TemplateEditor key={`${tpl.topic}-${tpl.language}`} tpl={tpl} />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ tpl }: { tpl: TemplateItem }) {
  const utils = trpc.useUtils();
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [format, setFormat] = useState<TemplateFormat>(tpl.format);

  useEffect(() => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    setFormat(tpl.format);
  }, [tpl.subject, tpl.body, tpl.format]);

  const save = trpc.emailTemplates.upsert.useMutation({
    onSuccess: () => {
      toast.success("Template saved");
      utils.emailTemplates.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Failed to save"),
  });

  const reset = trpc.emailTemplates.reset.useMutation({
    onSuccess: () => {
      toast.success("Reset to default");
      utils.emailTemplates.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Failed to reset"),
  });

  const switchFormat = (next: TemplateFormat) => {
    if (next === format) return;
    // Convert the body so no content is lost when toggling representations.
    setBody((b: string) => (next === "html" ? textToHtml(b) : htmlToText(b)));
    setFormat(next);
  };

  const dirty = subject !== tpl.subject || body !== tpl.body || format !== tpl.format;

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{tpl.label}</span>
          <Badge variant="outline" className="text-xs uppercase">
            {tpl.language}
          </Badge>
          <Badge
            variant="secondary"
            className={
              tpl.isDefault
                ? "bg-zinc-100 text-zinc-600"
                : "bg-emerald-100 text-emerald-700"
            }
          >
            {tpl.isDefault ? "default" : "custom"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {!tpl.isDefault && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => reset.mutate({ topic: tpl.topic, language: tpl.language })}
              disabled={reset.isPending}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() =>
              save.mutate({
                topic: tpl.topic,
                language: tpl.language,
                subject,
                body,
                format,
              })
            }
            disabled={save.isPending || !dirty || !subject.trim() || !body.trim()}
          >
            <Save className="mr-1 h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Subject
          </label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <label className="block text-xs font-medium text-muted-foreground">
              Body
            </label>
            <div className="inline-flex rounded-md border bg-background p-0.5 text-xs">
              {(["text", "html"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => switchFormat(f)}
                  className={
                    "px-2 py-0.5 rounded-sm font-medium transition-colors " +
                    (format === f
                      ? "bg-zinc-900 text-white"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {f === "text" ? "Plain text" : "HTML"}
                </button>
              ))}
            </div>
          </div>
          {format === "html" ? (
            <RichTextEditor
              value={body}
              onChange={setBody}
              allowImages
              placeholder="Email body (HTML — use the image button to add a GIF)"
              className="min-h-[200px]"
            />
          ) : (
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="font-mono text-sm"
              placeholder="Email body (plain text)"
            />
          )}
        </div>
      </div>
    </div>
  );
}
